import api from './api';
import { repeatArraySerializer } from './serializers';
import { ApiResponseDto } from '../common/interfaces/common.interface';
import {
  BillingItemDto,
  BillingSchemeDto,
  ChildBillingProfileDto,
  ChildBillingSummaryDto,
  GenerateInvoiceBatchDto,
  ChildBalancePageDto,
  PayerBalancePageDto,
  GetBalancesParams,
  InvoiceBatchDto,
  PaymentDto,
  SendInvoicesResultDto,
  InvoiceSendRecipientsDto,
  RecordPaymentDto,
  BillingSettingsDto,
  ChildContractDto,
  ContractNumberDto,
  ContractStatusCountsDto,
  PaymentPreviewDto,
  GenerationPreviewDto,
  InvoiceMonthSummaryDto,
  IssueInvoicesResultDto,
  NextContractNumberDto,
  CreateInvoiceDraftDto,
  GetBillingItemsParams,
  GetContractsParams,
  GetInvoicesParams,
  InvoiceDetailsDto,
  InvoiceDto,
  InvoicePreviewDto,
  PresignedFileDto,
  UpdateBillingSettingsDto,
  OverrideBillingItemDto,
  UpsertBillingItemDto,
  UpsertBillingSchemeDto,
  UpsertChildBillingProfileDto,
  UpdateInvoiceDraftDto,
  FinanceSummaryDto,
  FinanceTrendDto,
  FinanceTodoItemDto,
  FinanceNextMonthDto,
  FinanceCompositionDto,
  FinanceExportSheet,
  FinanceGroupSchemeDto,
  FinanceScopeParams,
  GetFinanceSummaryParams,
  GetFinanceTrendParams
} from '../common/interfaces/invoicing.interface';

/**
 * Every route is scoped by kindergarten. That is not decoration: the backend's
 * RolesGuard checks the admin against the :kindergartenId path param, so a route
 * without it would not be scoped at all.
 */
const base = (kindergartenId: string) => `/invoicing/kindergarten/${kindergartenId}`;

// ── billing settings ────────────────────────────────────────────────────────
export const getBillingSettings = async (kindergartenId: string): Promise<ApiResponseDto<BillingSettingsDto>> => {
  const response = await api.get(`${base(kindergartenId)}/settings`);
  return response.data;
};

export const updateBillingSettings = async (
  kindergartenId: string,
  dto: UpdateBillingSettingsDto
): Promise<ApiResponseDto<BillingSettingsDto>> => {
  const response = await api.put(`${base(kindergartenId)}/settings`, dto);
  return response.data;
};

// ── contract number and payer ───────────────────────────────────────────────
export const getContracts = async (
  kindergartenId: string,
  params: GetContractsParams = {}
): Promise<ApiResponseDto<ChildContractDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/contract`, {
    params: { page: params.page ?? 1, perPage: params.perPage ?? 10, ...params },
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

/** How many children each status chip would show, under the same search and groups. */
export const getContractCounts = async (
  kindergartenId: string,
  params: Pick<GetContractsParams, 'search' | 'groupIds'> = {}
): Promise<ApiResponseDto<ContractStatusCountsDto>> => {
  const response = await api.get(`${base(kindergartenId)}/contract/counts`, {
    params,
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

/**
 * The next free contract number under a pattern such as `UG-{godina}/{broj}`. Only a
 * suggestion: nothing is reserved, and setting it can still be refused if someone takes it
 * first.
 */
export const getNextContractNumber = async (kindergartenId: string, pattern?: string): Promise<ApiResponseDto<NextContractNumberDto>> => {
  const response = await api.get(`${base(kindergartenId)}/contract/next-number`, {
    params: pattern ? { pattern } : {}
  });
  return response.data;
};

export const setContractNumber = async (
  kindergartenId: string,
  childId: string,
  contractNumber: string,
  membershipId?: string
): Promise<ApiResponseDto<ContractNumberDto>> => {
  const response = await api.patch(`${base(kindergartenId)}/child/${childId}/contract`, {
    contractNumber,
    ...(membershipId ? { membershipId } : {})
  });
  return response.data;
};

export const setPayer = async (
  kindergartenId: string,
  childId: string,
  parentId: string,
  payerAddress?: string
): Promise<ApiResponseDto<void>> => {
  const response = await api.patch(`${base(kindergartenId)}/child/${childId}/payer`, {
    parentId,
    ...(payerAddress ? { payerAddress } : {})
  });
  return response.data;
};

/** The family's standing choice: whether the other parents get a copy of each invoice too. */
export const setInvoiceRecipients = async (
  kindergartenId: string,
  childId: string,
  toAllParents: boolean
): Promise<ApiResponseDto<{ invoiceToAllParents: boolean }>> => {
  const response = await api.patch(`${base(kindergartenId)}/child/${childId}/invoice-recipients`, { toAllParents });
  return response.data;
};

/**
 * Everything the child's Billing tab needs, in one request. Prefer this over calling
 * the contract, balance, invoice and payment routes separately.
 */
export const getChildBillingSummary = async (kindergartenId: string, childId: string): Promise<ApiResponseDto<ChildBillingSummaryDto>> => {
  const response = await api.get(`${base(kindergartenId)}/child/${childId}/billing-summary`);
  return response.data;
};

// ── invoices ────────────────────────────────────────────────────────────────
export const createInvoiceDraft = async (
  kindergartenId: string,
  childId: string,
  dto: CreateInvoiceDraftDto
): Promise<ApiResponseDto<InvoiceDetailsDto>> => {
  const response = await api.post(`${base(kindergartenId)}/child/${childId}/invoice`, dto);
  return response.data;
};

export const updateInvoiceDraft = async (
  kindergartenId: string,
  invoiceId: string,
  dto: UpdateInvoiceDraftDto
): Promise<ApiResponseDto<InvoiceDetailsDto>> => {
  const response = await api.patch(`${base(kindergartenId)}/invoice/${invoiceId}`, dto);
  return response.data;
};

export const deleteInvoiceDraft = async (kindergartenId: string, invoiceId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.delete(`${base(kindergartenId)}/invoice/${invoiceId}`);
  return response.data;
};

export const issueInvoice = async (
  kindergartenId: string,
  invoiceId: string,
  dto: { issueDate?: string; dueDate?: string } = {}
): Promise<ApiResponseDto<InvoiceDetailsDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/${invoiceId}/issue`, dto);
  return response.data;
};

export const cancelInvoice = async (
  kindergartenId: string,
  invoiceId: string,
  reason: string
): Promise<ApiResponseDto<InvoiceDetailsDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/${invoiceId}/cancel`, {
    reason
  });
  return response.data;
};

/**
 * Cancels an issued invoice and opens a new draft for the same month with the same lines:
 * how something is added to a month that already has its invoice.
 */
export const replaceInvoice = async (kindergartenId: string, invoiceId: string, reason: string): Promise<ApiResponseDto<InvoiceDetailsDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/${invoiceId}/replace`, { reason });
  return response.data;
};

export const getInvoicePreview = async (
  kindergartenId: string,
  childId: string,
  params: { periodYear: number; periodMonth: number }
): Promise<ApiResponseDto<InvoicePreviewDto>> => {
  const response = await api.get(`${base(kindergartenId)}/child/${childId}/invoice-preview`, { params });
  return response.data;
};

export const getInvoiceDetails = async (kindergartenId: string, invoiceId: string): Promise<ApiResponseDto<InvoiceDetailsDto>> => {
  const response = await api.get(`${base(kindergartenId)}/invoice/${invoiceId}`);
  return response.data;
};

export const getInvoices = async (kindergartenId: string, params: GetInvoicesParams = {}): Promise<ApiResponseDto<InvoiceDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/invoice`, {
    params: { page: params.page ?? 1, perPage: params.perPage ?? 10, ...params },
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

/** A month in numbers: contracts ready, drafts, issued, sent, paid, and the last run. */
export const getInvoiceMonth = async (
  kindergartenId: string,
  params: { periodYear: number; periodMonth: number; groupIds?: string[] }
): Promise<ApiResponseDto<InvoiceMonthSummaryDto>> => {
  const response = await api.get(`${base(kindergartenId)}/invoice-month`, { params, paramsSerializer: repeatArraySerializer });
  return response.data;
};

/** What generating the month would do, child by child, with nothing written. */
export const getGenerationPreview = async (
  kindergartenId: string,
  params: { periodYear: number; periodMonth: number; groupIds?: string[] }
): Promise<ApiResponseDto<GenerationPreviewDto>> => {
  const response = await api.get(`${base(kindergartenId)}/invoice-month/generation-preview`, {
    params,
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

/** Issues several drafts in one call; the report says, per invoice, what happened. */
export const issueInvoices = async (
  kindergartenId: string,
  dto: { invoiceIds: string[]; issueDate?: string; dueDate?: string; sendToPayer?: boolean }
): Promise<ApiResponseDto<IssueInvoicesResultDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/issue`, dto);
  return response.data;
};

/** The PDFs of several invoices as one zip; how many had none yet comes back alongside. */
export const downloadInvoicesZip = async (kindergartenId: string, invoiceIds: string[]): Promise<{ blob: Blob; skipped: number }> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/pdf-zip`, { invoiceIds }, { responseType: 'blob' });
  return { blob: response.data as Blob, skipped: Number(response.headers['x-skipped-count'] ?? 0) };
};

export const getInvoicePdfUrl = async (kindergartenId: string, invoiceId: string): Promise<ApiResponseDto<PresignedFileDto>> => {
  const response = await api.get(`${base(kindergartenId)}/invoice/${invoiceId}/pdf`);
  return response.data;
};

export const regenerateInvoicePdf = async (kindergartenId: string, invoiceId: string): Promise<ApiResponseDto<PresignedFileDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/${invoiceId}/pdf`);
  return response.data;
};

// ── catalogue ───────────────────────────────────────────────────────────────
export const getBillingItems = async (
  kindergartenId: string,
  params: GetBillingItemsParams = {}
): Promise<ApiResponseDto<BillingItemDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/billing-item`, {
    params: { page: params.page ?? 1, perPage: params.perPage ?? 10, ...params },
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

export const getActiveBillingItems = async (kindergartenId: string): Promise<ApiResponseDto<BillingItemDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/billing-item/active`);
  return response.data;
};

export const createBillingItem = async (kindergartenId: string, dto: UpsertBillingItemDto): Promise<ApiResponseDto<BillingItemDto>> => {
  const response = await api.post(`${base(kindergartenId)}/billing-item`, dto);
  return response.data;
};

export const updateBillingItem = async (
  kindergartenId: string,
  billingItemId: string,
  dto: UpsertBillingItemDto
): Promise<ApiResponseDto<BillingItemDto>> => {
  const response = await api.patch(`${base(kindergartenId)}/billing-item/${billingItemId}`, dto);
  return response.data;
};

export const deleteBillingItem = async (kindergartenId: string, billingItemId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.delete(`${base(kindergartenId)}/billing-item/${billingItemId}`);
  return response.data;
};

export const overrideBillingItem = async (
  kindergartenId: string,
  billingItemId: string,
  dto: OverrideBillingItemDto
): Promise<ApiResponseDto<BillingItemDto>> => {
  const response = await api.patch(`${base(kindergartenId)}/billing-item/${billingItemId}/override`, dto);
  return response.data;
};

// ── platform catalogue (super admin) ────────────────────────────────────────
export const getPlatformBillingItems = async (params: GetBillingItemsParams = {}): Promise<ApiResponseDto<BillingItemDto[]>> => {
  const response = await api.get('/invoicing/platform/billing-item', {
    params: { page: params.page ?? 1, perPage: params.perPage ?? 10, ...params },
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

export const createPlatformBillingItem = async (dto: UpsertBillingItemDto): Promise<ApiResponseDto<BillingItemDto>> => {
  const response = await api.post('/invoicing/platform/billing-item', dto);
  return response.data;
};

export const updatePlatformBillingItem = async (
  billingItemId: string,
  dto: UpsertBillingItemDto
): Promise<ApiResponseDto<BillingItemDto>> => {
  const response = await api.patch(`/invoicing/platform/billing-item/${billingItemId}`, dto);
  return response.data;
};

export const deletePlatformBillingItem = async (billingItemId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.delete(`/invoicing/platform/billing-item/${billingItemId}`);
  return response.data;
};

// ── schemes ─────────────────────────────────────────────────────────────────
export const getBillingSchemes = async (kindergartenId: string): Promise<ApiResponseDto<BillingSchemeDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/billing-scheme`);
  return response.data;
};

export const getBillingScheme = async (kindergartenId: string, schemeId: string): Promise<ApiResponseDto<BillingSchemeDto>> => {
  const response = await api.get(`${base(kindergartenId)}/billing-scheme/${schemeId}`);
  return response.data;
};

export const createBillingScheme = async (
  kindergartenId: string,
  dto: UpsertBillingSchemeDto
): Promise<ApiResponseDto<BillingSchemeDto>> => {
  const response = await api.post(`${base(kindergartenId)}/billing-scheme`, dto);
  return response.data;
};

export const updateBillingScheme = async (
  kindergartenId: string,
  schemeId: string,
  dto: UpsertBillingSchemeDto
): Promise<ApiResponseDto<BillingSchemeDto>> => {
  const response = await api.patch(`${base(kindergartenId)}/billing-scheme/${schemeId}`, dto);
  return response.data;
};

export const deleteBillingScheme = async (kindergartenId: string, schemeId: string): Promise<ApiResponseDto<void>> => {
  const response = await api.delete(`${base(kindergartenId)}/billing-scheme/${schemeId}`);
  return response.data;
};

/** null clears the scheme from the group. */
export const assignSchemeToGroup = async (
  kindergartenId: string,
  groupId: string,
  billingSchemeId: string | null
): Promise<ApiResponseDto<void>> => {
  const response = await api.put(`${base(kindergartenId)}/group/${groupId}/billing-scheme`, {
    billingSchemeId
  });
  return response.data;
};

export const getChildBillingProfile = async (
  kindergartenId: string,
  childId: string
): Promise<ApiResponseDto<ChildBillingProfileDto | null>> => {
  const response = await api.get(`${base(kindergartenId)}/child/${childId}/billing-profile`);
  return response.data;
};

export const getChildBillingProfileHistory = async (
  kindergartenId: string,
  childId: string
): Promise<ApiResponseDto<ChildBillingProfileDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/child/${childId}/billing-profile/history`);
  return response.data;
};

export const upsertChildBillingProfile = async (
  kindergartenId: string,
  childId: string,
  dto: UpsertChildBillingProfileDto
): Promise<ApiResponseDto<ChildBillingProfileDto>> => {
  const response = await api.put(`${base(kindergartenId)}/child/${childId}/billing-profile`, dto);
  return response.data;
};

export const closeChildBillingProfile = async (kindergartenId: string, childId: string, endDate: string): Promise<ApiResponseDto<null>> => {
  const response = await api.delete(`${base(kindergartenId)}/child/${childId}/billing-profile`, {
    data: { endDate }
  });
  return response.data;
};

export const generateInvoiceBatch = async (
  kindergartenId: string,
  dto: GenerateInvoiceBatchDto
): Promise<ApiResponseDto<InvoiceBatchDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice-batch`, dto);
  return response.data;
};

export const issueInvoiceBatch = async (kindergartenId: string, batchId: string): Promise<ApiResponseDto<InvoiceBatchDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice-batch/${batchId}/issue`);
  return response.data;
};

export const getInvoiceBatches = async (kindergartenId: string): Promise<ApiResponseDto<InvoiceBatchDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/invoice-batch`);
  return response.data;
};

export const getInvoiceBatch = async (kindergartenId: string, batchId: string): Promise<ApiResponseDto<InvoiceBatchDto>> => {
  const response = await api.get(`${base(kindergartenId)}/invoice-batch/${batchId}`);
  return response.data;
};

export const discardInvoiceBatch = async (kindergartenId: string, batchId: string): Promise<ApiResponseDto<null>> => {
  const response = await api.delete(`${base(kindergartenId)}/invoice-batch/${batchId}`);
  return response.data;
};

/** Where a payment of `amount` would go, by the rule recording uses; 0 lists what is owed. */
export const previewPayment = async (kindergartenId: string, childId: string, amount: number): Promise<ApiResponseDto<PaymentPreviewDto>> => {
  const response = await api.post(`${base(kindergartenId)}/child/${childId}/payment/preview`, { amount });
  return response.data;
};

export const recordPayment = async (
  kindergartenId: string,
  childId: string,
  dto: RecordPaymentDto
): Promise<ApiResponseDto<PaymentDto>> => {
  const response = await api.post(`${base(kindergartenId)}/child/${childId}/payment`, dto);
  return response.data;
};

export const getChildPayments = async (kindergartenId: string, childId: string): Promise<ApiResponseDto<PaymentDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/child/${childId}/payment`);
  return response.data;
};

export const removePayment = async (kindergartenId: string, paymentId: string): Promise<ApiResponseDto<null>> => {
  const response = await api.delete(`${base(kindergartenId)}/payment/${paymentId}`);
  return response.data;
};

/**
 * Paged on the server, including the debtors filter and the ordering - a caller that
 * filtered or sorted here would get short pages and the wrong children first.
 *
 * `childIds` opts out of paging: a caller that already has a page of children asks for
 * exactly those balances.
 */
export const getBalances = async (kindergartenId: string, params: GetBalancesParams = {}): Promise<ApiResponseDto<ChildBalancePageDto>> => {
  const response = await api.get(`${base(kindergartenId)}/balance`, {
    params: { page: params.page ?? 1, perPage: params.perPage ?? 10, ...params },
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

/**
 * The same debts rolled up to whoever settles them, paged by family rather than by
 * child - a parent paying for two children pays one sum.
 */
export const getBalancesByPayer = async (
  kindergartenId: string,
  params: GetBalancesParams = {}
): Promise<ApiResponseDto<PayerBalancePageDto>> => {
  const response = await api.get(`${base(kindergartenId)}/balance/by-payer`, {
    params: { page: params.page ?? 1, perPage: params.perPage ?? 10, ...params },
    paramsSerializer: repeatArraySerializer
  });
  return response.data;
};

/**
 * The debts list as a CSV file: the same filters, every row, or only `childIds` /
 * `payerIds` when rows were chosen.
 */
export const exportBalances = async (kindergartenId: string, params: GetBalancesParams & { view: 'child' | 'payer' }): Promise<Blob> => {
  const response = await api.get(`${base(kindergartenId)}/balance/export`, {
    params,
    paramsSerializer: repeatArraySerializer,
    responseType: 'blob'
  });
  return response.data as Blob;
};

/**
 * `toAllParents` overrides the family's choice on the contract for this send, either way.
 * Left out, each invoice follows its own child's contract - which is not the same as
 * sending `false`.
 */
export const sendInvoice = async (
  kindergartenId: string,
  invoiceId: string,
  toAllParents?: boolean
): Promise<ApiResponseDto<SendInvoicesResultDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/${invoiceId}/send`, toAllParents === undefined ? {} : { toAllParents });
  return response.data;
};

export const sendInvoices = async (
  kindergartenId: string,
  invoiceIds: string[],
  toAllParents?: boolean
): Promise<ApiResponseDto<SendInvoicesResultDto>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/send`, {
    invoiceIds,
    ...(toAllParents === undefined ? {} : { toAllParents })
  });
  return response.data;
};

/** Who each invoice would be emailed to, the payer and the other parents, before sending. At most 200. */
export const getSendRecipients = async (
  kindergartenId: string,
  invoiceIds: string[]
): Promise<ApiResponseDto<InvoiceSendRecipientsDto[]>> => {
  const response = await api.post(`${base(kindergartenId)}/invoice/send/recipients`, { invoiceIds });
  return response.data;
};

/**
 * Every figure the Finance screens show, aggregated by the server at whichever level is
 * asked for. The client never adds these up itself: the rounding is the backend's, and a
 * total summed from rows would report part of the money as all of it.
 */
export const getFinanceSummary = async (
  kindergartenId: string,
  params: GetFinanceSummaryParams
): Promise<ApiResponseDto<FinanceSummaryDto>> => {
  const response = await api.get(`${base(kindergartenId)}/finance/summary`, { params });
  return response.data;
};

/** What needs doing today, most urgent first. */
export const getFinanceTodo = async (
  kindergartenId: string,
  scope: FinanceScopeParams & { year?: number; month?: number } = {}
): Promise<ApiResponseDto<FinanceTodoItemDto[]>> => {
  const response = await api.get(`${base(kindergartenId)}/finance/todo`, { params: scope });
  return response.data;
};

/** A group's price list for its page; null when it has none. */
export const getFinanceGroupScheme = async (kindergartenId: string, groupId: string): Promise<ApiResponseDto<FinanceGroupSchemeDto | null>> => {
  const response = await api.get(`${base(kindergartenId)}/finance/group/${groupId}/scheme`);
  return response.data;
};

/** Next month from today, as the contracts would invoice it. */
export const getFinanceNextMonth = async (kindergartenId: string, scope: FinanceScopeParams = {}): Promise<ApiResponseDto<FinanceNextMonthDto>> => {
  const response = await api.get(`${base(kindergartenId)}/finance/next-month`, { params: scope });
  return response.data;
};

export const getFinanceComposition = async (
  kindergartenId: string,
  params: GetFinanceSummaryParams
): Promise<ApiResponseDto<FinanceCompositionDto>> => {
  const response = await api.get(`${base(kindergartenId)}/finance/composition`, { params });
  return response.data;
};

/** The server's name for a download, from Content-Disposition; `fallback` when it sends none. */
const fileNameOf = (headers: Record<string, string | undefined>, fallback: string): string => {
  const header = headers['content-disposition'] ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) return decodeURIComponent(encoded[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : fallback;
};

const download = async (url: string, params: Record<string, unknown>, fallback: string): Promise<{ blob: Blob; fileName: string }> => {
  const response = await api.get(url, { params, responseType: 'blob' });
  return { blob: response.data as Blob, fileName: fileNameOf(response.headers as Record<string, string>, fallback) };
};

/** The bookkeeper's workbook for whole months `from`..`to` (YYYY-MM). */
export const downloadFinanceWorkbook = (kindergartenId: string, from: string, to: string, sheets: FinanceExportSheet[]) =>
  download(`${base(kindergartenId)}/finance/export`, { from, to, sheets: sheets.join(','), format: 'xlsx' }, 'finansije.xlsx');

/** The period's invoice PDFs, as a zip of its own. */
export const downloadFinancePdfZip = (kindergartenId: string, from: string, to: string, scope: FinanceScopeParams = {}) =>
  download(`${base(kindergartenId)}/finance/export/pdf`, { from, to, ...scope }, 'racuni.zip');

export const downloadInvoicesCsv = (kindergartenId: string, year: number, month: number, scope: FinanceScopeParams = {}) =>
  download(`${base(kindergartenId)}/finance/export/invoices`, { year, month, ...scope }, 'racuni.csv');

export const downloadPaymentsCsv = (kindergartenId: string, year: number, month: number, scope: FinanceScopeParams = {}) =>
  download(`${base(kindergartenId)}/finance/export/payments`, { year, month, ...scope }, 'uplate.csv');

/** Charged against received, month by month, for the same scope. Carries no debt - see the route. */
export const getFinanceTrend = async (kindergartenId: string, params: GetFinanceTrendParams): Promise<ApiResponseDto<FinanceTrendDto>> => {
  const response = await api.get(`${base(kindergartenId)}/finance/trend`, { params });
  return response.data;
};
